import { IGlobalStorefrontSettings, IStorefrontSettings, ShowLinkedItemsMode } from '../models/Storefront';

/**
 * Single-source-of-truth mirror of the API's "Show Linked Items" cascade.
 *
 *   globalMode = global?.showLinkedItemsMode ?? 'all'
 *   if (globalMode === 'disabled')              -> blocked (hard, all storefronts)
 *   mode(sf) = sf.showLinkedItemsMode ?? globalMode
 *   if (mode(sf) === 'disabled')                -> blocked for that storefront (hard)
 *   default = mode === 'all'
 *   effective = entry.showLinkedItems ?? product.showLinkedItemsOnStorefront ?? default
 */

const DEFAULT_MODE: ShowLinkedItemsMode = 'all';

/** Display names for the cascade modes, shared by every "Show Linked Items" control. */
export const SHOW_LINKED_ITEMS_MODE_TEXT: Record<ShowLinkedItemsMode, string> = {
    all: 'All Products',
    select: 'Select Products',
    disabled: 'Disabled',
};

/** Effective global mode; unset/legacy-null defaults to 'all'. */
export function globalMode(global?: IGlobalStorefrontSettings): ShowLinkedItemsMode {
    return global?.showLinkedItemsMode ?? DEFAULT_MODE;
}

/** True when linked items are hard-disabled globally (blocks every storefront). */
export function isGloballyDisabled(global?: IGlobalStorefrontSettings): boolean {
    return globalMode(global) === 'disabled';
}

/** Effective mode for a storefront: its own mode, else inherit the global mode. */
export function storefrontMode(global?: IGlobalStorefrontSettings, sf?: IStorefrontSettings): ShowLinkedItemsMode {
    return sf?.showLinkedItemsMode ?? globalMode(global);
}

/** True when linked items are hard-disabled for this storefront (globally or at the storefront level). */
export function isStorefrontDisabled(global?: IGlobalStorefrontSettings, sf?: IStorefrontSettings): boolean {
    if (isGloballyDisabled(global)) return true;
    return storefrontMode(global, sf) === 'disabled';
}

/**
 * Whether linked items show by default (before product/entry overrides) for the given
 * storefront's effective mode. Pass no storefront to get the global-level default.
 */
export function defaultShow(global?: IGlobalStorefrontSettings, sf?: IStorefrontSettings): boolean {
    return storefrontMode(global, sf) === 'all';
}

/** Effective show/hide for product P on storefront S. */
export function effectiveShowLinkedItems(
    global: IGlobalStorefrontSettings | undefined,
    sf: IStorefrontSettings | undefined,
    productShow: boolean | null | undefined,
    entryShow: boolean | null | undefined
): boolean {
    if (isGloballyDisabled(global)) return false;
    const mode = storefrontMode(global, sf);
    if (mode === 'disabled') return false;
    const def = mode === 'all';
    return entryShow ?? productShow ?? def;
}
